import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { createMRTColumnHelper, MRT_ColumnDef } from 'material-react-table'
import { useMemo } from 'react'

import { TextCell } from '@/components'
import { createHeaderWithTooltip } from '@/components/MrtTable/helpers'
import { ReleaseDto } from '@/endpoints/models'
import { useAppContext } from '@/hooks'
import { useDetailTabContext } from '@/pages/User/pages/Home/components/DetailTab'

import { ExtEnvironmentDeploymentDto } from '../types'
import { useReleaseRedirect } from './useReleaseRedirect'

export const useDeploymentsColumns = () => {
	const { t } = useAppContext()
	const {
		state: { node, editMode },
	} = useDetailTabContext()

	const columnHelper = createMRTColumnHelper<ExtEnvironmentDeploymentDto>()

	const onReleaseRedirect = useReleaseRedirect(node)

	const columns = useMemo(
		() => [
			columnHelper.accessor('objectType', {
				...createHeaderWithTooltip(t('OBJECT_TYPE')),
				size: 50,
				Cell: ({ cell, table, row, column }) => (
					<TextCell
						cell={cell}
						column={column}
						table={table}
						row={row}
						isReadOnly
						customValue={cell?.getValue()?.name}
						isDisabled={!editMode}
					/>
				),
			}),
			columnHelper.accessor('name', {
				...createHeaderWithTooltip(t('ENVIRONMENT')),
				size: 50,
				Cell: ({ cell, table, row, column }) => (
					<TextCell
						cell={cell}
						column={column}
						table={table}
						row={row}
						isReadOnly
						isDisabled={!editMode}
					/>
				),
			}),
			columnHelper.accessor('lastDeploymentStructureHistoryVersion', {
				...createHeaderWithTooltip(t('DEPLOYED_VERSION')),
				size: 50,
				Cell: ({ cell, table, row, column }) => (
					<TextCell
						cell={cell}
						column={column}
						table={table}
						row={row}
						isReadOnly
						isDisabled={!editMode}
					/>
				),
			}),
			columnHelper.accessor('lastDeploymentAuthorCompositeName', {
				...createHeaderWithTooltip(t('DEPLOYED_BY')),
				size: 50,
				Cell: ({ cell, table, row, column }) => (
					<TextCell
						cell={cell}
						column={column}
						table={table}
						row={row}
						isReadOnly
						isDisabled={!editMode}
					/>
				),
			}),
			columnHelper.accessor('lastRelease', {
				...createHeaderWithTooltip(t('DEPLOYMENT_PACKAGE')),
				size: 50,
				Cell: ({ cell, table, row, column }) => {
					const cellNameValue = cell?.getValue()?.name

					return cellNameValue ? (
						<div
							className="cursor-pointer"
							data-testid="deployments-package-link"
							onClick={() => onReleaseRedirect(cell?.getValue() as ReleaseDto)}
						>
							<FontAwesomeIcon icon={faEdit} className="mr-1" />
							{cellNameValue}
						</div>
					) : (
						<TextCell
							cell={cell}
							column={column}
							table={table}
							row={row}
							isReadOnly
							isDisabled={!editMode}
						/>
					)
				},
			}),
		],
		[columnHelper, onReleaseRedirect, t, editMode],
	)

	return columns as MRT_ColumnDef<ExtEnvironmentDeploymentDto>[]
}
